import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from '../utils/toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const CreateTask = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: '',
    assignedTo: [],
    assignedGroups: [],
    deadline: '',
    status: 'pending',
    verificationTypes: []
  });
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);

  useEffect(() => {
    fetchClients();
    fetchUsers();
    fetchGroups();
    fetchVerificationTypes();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, clients]);

  useEffect(() => {
    if (userSearchTerm) {
      const filtered = users.filter(user =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
      setShowUserDropdown(true);
    } else {
      setFilteredUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearchTerm, users]);

  useEffect(() => {
    if (groupSearchTerm) {
      const filtered = groups.filter(group =>
        group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
      );
      setFilteredGroups(filtered);
      setShowGroupDropdown(true);
    } else {
      setFilteredGroups([]);
      setShowGroupDropdown(false);
    }
  }, [groupSearchTerm, groups]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/master-data');
      // Handle new response format with success wrapper
      if (response.data.success) {
        setClients(response.data.data || []);
      } else {
        // Fallback for old format
        setClients(response.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to fetch groups');
    }
  };

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch verification types:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientSelect = (clientId) => {
    setFormData(prev => ({
      ...prev,
      client: clientId
    }));
    const selectedClient = clients.find(c => c._id === clientId);
    setSearchTerm(selectedClient ? selectedClient.clientName : '');
    setShowSuggestions(false);
  };

  const handleUserSelect = (userId) => {
    if (userId && !formData.assignedTo.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        assignedTo: [...prev.assignedTo, userId]
      }));
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleGroupSelect = (groupId) => {
    if (groupId && !formData.assignedGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        assignedGroups: [...prev.assignedGroups, groupId]
      }));
    }
    setGroupSearchTerm('');
    setShowGroupDropdown(false);
  };

  const removeUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(id => id !== userId)
    }));
  };

  const removeGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.filter(id => id !== groupId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Format verification types to match the expected schema
      const formattedVerificationTypes = formData.verificationTypes.map(typeId => {
        const verificationType = verificationTypes.find(vt => vt._id === typeId);
        return {
          value: verificationType ? verificationType.name : '',
          id: typeId
        };
      });

      const taskData = {
        ...formData,
        verificationTypes: formattedVerificationTypes
      };

      const response = await api.post('/tasks', taskData);
      toast.success('Task created successfully');
      // Redirect to the created task detail page
      navigate(`/tasks/${response.data._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedUsers = () => {
    return formData.assignedTo.map(userId => {
      const user = users.find(u => u._id === userId);
      return user ? { id: userId, name: `${user.firstName} ${user.lastName}` } : null;
    }).filter(Boolean);
  };

  const getSelectedGroups = () => {
    return formData.assignedGroups.map(groupId => {
      const group = groups.find(g => g._id === groupId);
      return group ? { id: groupId, name: group.name } : null;
    }).filter(Boolean);
  };

  const getSelectedClient = () => {
    return clients.find(c => c._id === formData.client);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/tasks')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Create New Task</h1>
          </div>
          <p className="text-gray-600">Fill in the details below to create a new task.</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Task Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter task name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter task description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                  required
                />
              </div>

              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search for a client..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showSuggestions && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredClients.map((client) => (
                        <div
                          key={client._id}
                          onClick={() => handleClientSelect(client._id)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {client.clientName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {getSelectedClient() && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <span className="text-sm text-blue-800">
                      Selected: {getSelectedClient().clientName}
                    </span>
                  </div>
                )}
              </div>

              {/* Assigned Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign To Users
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Search for users..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredUsers.map((user) => (
                        <div
                          key={user._id}
                          onClick={() => handleUserSelect(user._id)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {user.firstName} {user.lastName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {getSelectedUsers().length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {getSelectedUsers().map((user) => (
                      <span
                        key={user.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {user.name}
                        <button
                          type="button"
                          onClick={() => removeUser(user.id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Assigned Groups */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign To Groups
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    placeholder="Search for groups..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showGroupDropdown && filteredGroups.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredGroups.map((group) => (
                        <div
                          key={group._id}
                          onClick={() => handleGroupSelect(group._id)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {group.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {getSelectedGroups().length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {getSelectedGroups().map((group) => (
                      <span
                        key={group.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        {group.name}
                        <button
                          type="button"
                          onClick={() => removeGroup(group.id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Verification Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Types
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {verificationTypes.map((type) => (
                    <label key={type._id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.verificationTypes.includes(type._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              verificationTypes: [...prev.verificationTypes, type._id]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              verificationTypes: prev.verificationTypes.filter(id => id !== type._id)
                            }));
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{type.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Form Actions */}
            <div className="mt-8 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form> 
        </div>
      </div>
    </div>
  );
};

export default CreateTask; 